import { useState, useId, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useCardSurchargePercent } from "@/hooks/useCardSurchargePercent";
import { cardSurchargeCents, splitCardChargeCents } from "../../../supabase/functions/_shared/square-order-body.ts";
import { rankHandLoggedMatches } from "../../../supabase/functions/_shared/square-payment-claim.ts";
import { CARD_METHODS } from "./paymentMethods";

interface RecordPaymentFormOptions {
  /** Load the card fee and the Square-recorded payments only while shown. */
  enabled: boolean;
  quoteId: string;
  quoteTotal: number;
  amountPaid: number;
  /** The starting method, and the one a successful save returns to. */
  initialMethod: string;
  /** After the payment is recorded, before the form resets. */
  onRecorded: () => void;
}

/**
 * RecordPaymentDialog's state, Square-match query and insert, shared with the
 * quote drawer's inline record panel (#1559).
 */
export function useRecordPaymentForm({
  enabled,
  quoteId,
  quoteTotal,
  amountPaid,
  initialMethod,
  onRecorded,
}: RecordPaymentFormOptions) {
  const balance = Math.max(0, quoteTotal - amountPaid);
  const balanceCents = Math.round(balance * 100);
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [noCardFee, setNoCardFee] = useState(false);
  const [method, setMethod] = useState(initialMethod);
  const [reference, setReference] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const { user } = useAuth();
  const qc = useQueryClient();
  const fieldId = useId();
  const idFor = (name: string) => `${fieldId}-${name}`;
  const { percent, status: feeStatus } = useCardSurchargePercent(enabled);

  // For a card payment the entered figure is the gross charge, so it splits
  // into what counts toward the invoice (amount) and the card fee (fee_amount).
  // Until staff type a figure, a card payment is prefilled with the balance
  // plus its fee, so accepting the prefill credits exactly the balance.
  const isCard = CARD_METHODS.has(method);
  const applyFee = isCard && !noCardFee;
  const defaultAmount = applyFee
    ? ((balanceCents + cardSurchargeCents(balanceCents, percent)) / 100).toFixed(2)
    : balance.toFixed(2);
  const displayedAmount = amountTouched ? amount : defaultAmount;
  const enteredCents = Math.round(parseFloat(displayedAmount) * 100);
  const split = useMemo(
    () => (applyFee ? splitCardChargeCents(enteredCents, percent) : { netCents: enteredCents, feeCents: 0 }),
    [applyFee, enteredCents, percent],
  );
  // Never save a card payment without the fee split.
  const feeUnavailable = applyFee && feeStatus !== "success";
  const creditedCents = Number.isFinite(split.netCents) && split.netCents > 0 ? split.netCents : 0;

  // Payments Square already recorded on this quote. A card payment matching
  // one of them (#1217's same-amount, ±3-day rule) is probably the same money
  // logged a second time (#1220).
  const squarePayments = useQuery({
    queryKey: ["quote_payments", quoteId, "square-recorded"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_payments")
        .select("id, amount, fee_amount, payment_date, square_payment_id")
        .eq("quote_id", quoteId)
        .not("square_payment_id", "is", null);
      if (error) throw error;
      return data ?? [];
    },
    enabled: enabled && isCard,
    // The app's 30s default would hide a webhook row that landed since the
    // last look; re-check every time the dialog opens or a card method is picked.
    staleTime: 0,
  });
  const squareMatch = useMemo(() => {
    const rows = squarePayments.data;
    if (!isCard || creditedCents <= 0 || !rows) return null;
    const best = rankHandLoggedMatches(rows, creditedCents, paymentDate)[0];
    // The ranker returns its own narrow row type; take the full row back by id
    // so its square_payment_id is in hand for the note.
    return best ? rows.find((r) => r.id === best.id) ?? null : null;
  }, [isCard, creditedCents, squarePayments.data, paymentDate]);

  const resetAmount = () => {
    setAmount("");
    setAmountTouched(false);
    setNoCardFee(false);
    setPaymentDate(new Date().toISOString().split("T")[0]);
  };

  // A typed figure is kept across a method change: the amount field sits above
  // the method, so staff often type the card charge before picking Credit Card.
  // "No card fee" only applies to card methods, so leaving one clears it.
  const handleMethodChange = (next: string) => {
    if (!CARD_METHODS.has(next)) setNoCardFee(false);
    setMethod(next);
  };

  const recordPayment = useMutation({
    mutationFn: async () => {
      if (!Number.isFinite(enteredCents) || enteredCents <= 0) throw new Error("Enter a valid amount");
      if (feeUnavailable) throw new Error("Couldn't load the card fee setting — reload to record a card payment");
      // Marks a confirmed override, so the duplicate-pair query can tell it
      // from a missed warning.
      const overrideNote = squareMatch ? `Recorded anyway: matches Square payment ${squareMatch.square_payment_id}` : null;

      const { error } = await supabase
        .from("quote_payments")
        .insert({
          quote_id: quoteId,
          amount: split.netCents / 100,
          fee_amount: split.feeCents / 100,
          method,
          reference: reference.trim() || null,
          notes: [notes.trim(), overrideNote].filter(Boolean).join("\n") || null,
          recorded_by: user?.id ?? null,
          payment_date: paymentDate,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote_payments", quoteId] });
      toast.success("Payment recorded");
      onRecorded();
      // Reset
      resetAmount();
      setMethod(initialMethod);
      setReference("");
      setNotes("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    balance,
    displayedAmount,
    setAmount: (value: string) => {
      setAmount(value);
      setAmountTouched(true);
    },
    isCard,
    noCardFee,
    setNoCardFee,
    applyFee,
    split,
    percent,
    feeStatus,
    method,
    handleMethodChange,
    reference,
    setReference,
    paymentDate,
    setPaymentDate,
    notes,
    setNotes,
    squareMatch,
    squarePaymentsError: squarePayments.isError,
    creditedCents,
    feeUnavailable,
    resetAmount,
    submit: () => recordPayment.mutate(),
    isPending: recordPayment.isPending,
    idFor,
  };
}
